import { useItems } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { CreateActionButton } from '@components/Buttons';
import { DataGrid } from '@components/DataGrid';
import { Box } from '@mui/system';
import { DataGridProProps, GridRowParams } from '@mui/x-data-grid-pro';
import { getRolesColumns } from '@utils/tables';
import { ROLES_FIELDS } from 'constants/global';
import React from 'react';
import { IRole } from 'types/authorization';
import { IDataGridProps } from 'types/common';
import RoleCreateDialog from './Dialogs/Roles/RoleCreateDialog';
import RoleDeleteDialog from './Dialogs/Roles/RoleDeleteDialog';
import RoleEditDialog from './Dialogs/Roles/RoleEditDialog';

const VISIBILITY_MODEL = {
	[ROLES_FIELDS.id]: false,
	[ROLES_FIELDS.version]: false,
	[ROLES_FIELDS.modifiedTime]: false
};

const RolesTable: React.FC<Partial<IDataGridProps & DataGridProProps>> = ({
	...dataGridProps
}) => {
	const { data, isLoading } = useItems<IRole>(
		QueryKeys.roles,
		API_ROUTES.v1.roles.all
	);
	const columns = React.useMemo(() => getRolesColumns(), []);

	const tableActions = React.useMemo(
		() => [
			<CreateActionButton
				key="add-role-key"
				Dialog={RoleCreateDialog}
				buttonLabel={intl.tr('roles.table.action.add.button')}
			/>
		],
		[]
	);

	const getRowActions = (p: GridRowParams) => {
		if (p?.row?.systemDefault) {
			return [];
		}
		return [
			{
				key: 'edit',
				label: intl.tr('button.edit'),
				Dialog: RoleEditDialog
			},
			{
				key: 'delete',
				label: intl.tr('button.delete'),
				Dialog: RoleDeleteDialog
			}
		];
	};

	return (
		<Box display="flex" flexGrow={1} data-testid="roles-table">
			<DataGrid
				checkboxSelection={false}
				noDataMessageId="grid.data.no.data.roles"
				rows={data?.data || []}
				loading={isLoading}
				getRowActions={getRowActions}
				columns={columns}
				actions={tableActions}
				columnVisibilityModel={VISIBILITY_MODEL}
				initialState={{
					sorting: {
						sortModel: [{ field: ROLES_FIELDS.createdTime, sort: 'desc' }]
					}
				}}
				{...dataGridProps}
			/>
		</Box>
	);
};

export default RolesTable;
